import "@expo/metro-runtime";
import { registerRootComponent } from "expo";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, SafeAreaView, StyleSheet, Text, TextInput, View } from "react-native";
import "react-native-get-random-values";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { ExpoRoot } from "./ExpoRoot";
import { ExpoRootNextBestAction } from "./ExpoRootNextBestAction";
import { CUSTOMER, locale } from "./SharedBootstrap";
import { fetchConfiguration } from "./devConfiguration";
import { devSettings } from "./devSettings";
import type { DevSettings } from "./devSettings";
import { NativeTestRoot } from "./testVariant/NativeTestRoot";
import { isNativeTestVariant, redirectTranslationsToMockServer } from "./testVariant/nativeTestVariant";

redirectTranslationsToMockServer();

const fetchConfiguredAuthToken = async (): Promise<string> => {
  const configuration = await fetchConfiguration();

  return typeof configuration.auth_token === "string" ? configuration.auth_token : "";
};

interface RequestedRootProps extends DevSettings {
  readonly getAuthToken?: () => Promise<string>;
}

const RequestedRoot = ({
  root,
  tradename,
  locale,
  country,
  segment,
  customerId,
  getAuthToken = fetchConfiguredAuthToken,
}: RequestedRootProps) => {
  const customer = useMemo(
    () => ({ customerId: customerId ?? CUSTOMER[tradename].customerId, country, segment }),
    [customerId, tradename, country, segment],
  );
  const Root = root === "next-best-action" ? ExpoRootNextBestAction : ExpoRoot;

  return <Root customer={customer} getAuthToken={getAuthToken} locale={locale} tradename={tradename} />;
};

const getTestAuthToken = () => Promise.resolve("");

const DevRoot = () => {
  const [selectedRoot, setSelectedRoot] = useState<"ExpoRoot" | "ExpoRootNextBestAction" | null>(null);
  const [tradename, setTradename] = useState<Tradename>(Tradename.LOOKIERO);
  const [customerId, setCustomerId] = useState<string>(CUSTOMER[Tradename.LOOKIERO].customerId);
  const [authToken, setAuthToken] = useState<string>("");

  useEffect(() => {
    let isCancelled = false;

    fetchConfiguredAuthToken().then((configuredAuthToken) => {
      if (!isCancelled) {
        setAuthToken(configuredAuthToken);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, []);

  const currentCustomer = useMemo(
    () => ({
      ...CUSTOMER[tradename],
      customerId,
    }),
    [tradename, customerId],
  );

  const getAuthToken = useCallback(() => Promise.resolve(authToken), [authToken]);

  const handleTradenameChange = useCallback((newTradename: Tradename) => {
    setTradename(newTradename);
    setCustomerId(CUSTOMER[newTradename].customerId);
  }, []);

  const handleSetLookiero = useCallback(() => handleTradenameChange(Tradename.LOOKIERO), [handleTradenameChange]);
  const handleSetOutfittery = useCallback(() => handleTradenameChange(Tradename.OUTFITTERY), [handleTradenameChange]);

  const handleSelectExpoRoot = useCallback(() => setSelectedRoot("ExpoRoot"), []);
  const handleSelectExpoRootNextBestAction = useCallback(() => setSelectedRoot("ExpoRootNextBestAction"), []);

  if (selectedRoot === "ExpoRoot") {
    return <ExpoRoot customer={currentCustomer} getAuthToken={getAuthToken} locale={locale} tradename={tradename} />;
  }

  if (selectedRoot === "ExpoRootNextBestAction") {
    return (
      <ExpoRootNextBestAction
        customer={currentCustomer}
        getAuthToken={getAuthToken}
        locale={locale}
        tradename={tradename}
      />
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <Text style={styles.title}>Select Root Component</Text>

      <View style={styles.section}>
        <Text style={styles.label}>Tradename:</Text>
        <View style={styles.row}>
          <Button
            color={tradename === Tradename.LOOKIERO ? "#007AFF" : "#808080"}
            title="LOOKIERO"
            onPress={handleSetLookiero}
          />
          <View style={styles.spacer} />
          <Button
            color={tradename === Tradename.OUTFITTERY ? "#007AFF" : "#808080"}
            title="OUTFITTERY"
            onPress={handleSetOutfittery}
          />
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Customer ID:</Text>
        <TextInput
          placeholder="Enter Customer ID"
          style={styles.input}
          value={customerId}
          onChangeText={setCustomerId}
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Auth Token:</Text>
        <TextInput
          numberOfLines={3}
          placeholder="Enter Auth Token"
          style={[styles.input, styles.textArea]}
          value={authToken}
          multiline
          onChangeText={setAuthToken}
        />
      </View>

      <View style={styles.buttonContainer}>
        <Button title="Style Profile" onPress={handleSelectExpoRoot} />
      </View>
      <View style={styles.buttonContainer}>
        <Button title="Next Best Action" onPress={handleSelectExpoRootNextBestAction} />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    marginVertical: 10,
    width: "80%",
  },
  container: {
    alignItems: "center",
    backgroundColor: "#fff",
    flex: 1,
    justifyContent: "center",
  },
  input: {
    borderColor: "gray",
    borderWidth: 1,
    height: 40,
    marginTop: 5,
    paddingHorizontal: 10,
    width: "100%",
  },
  label: {
    fontSize: 16,
    fontWeight: "bold",
  },
  row: {
    flexDirection: "row",
    marginTop: 5,
  },
  section: {
    marginBottom: 20,
    width: "80%",
  },
  spacer: {
    width: 20,
  },
  textArea: {
    height: 80,
    textAlignVertical: "top",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 20,
  },
});

const StandaloneRoot = () =>
  isNativeTestVariant ? (
    <NativeTestRoot>{(settings) => <RequestedRoot {...settings} getAuthToken={getTestAuthToken} />}</NativeTestRoot>
  ) : devSettings ? (
    <RequestedRoot {...devSettings} />
  ) : (
    <DevRoot />
  );

registerRootComponent(StandaloneRoot);
